import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Job, Site } from '../../types/site';
import {
  StockRequest,
  formatRequestRef,
  requestReceiveProgress,
  useStockRequestStore,
} from '../../store/stockRequestStore';
import { resolveJobTitle } from '../Stock/requests/RequestJobLinkBar';
import { fmtStatusLabel, statusBadge } from '../Stock/requests/theme';

interface Props {
  job: Job;
  site: Site;
  sites: Site[];
  isDark: boolean;
  text: string;
  muted: string;
  border: string;
  divider: string;
  /** When set, loads stock requests linked to any of these job ids (e.g. recurring series). */
  relatedJobIds?: string[];
}

const JobStockRequestsSection: React.FC<Props> = ({
  job,
  site,
  sites,
  isDark,
  text,
  muted,
  border,
  divider,
  relatedJobIds,
}) => {
  const fetchRequestsForJob = useStockRequestStore(s => s.fetchRequestsForJob);
  const fetchRequests = useStockRequestStore(s => s.fetchRequests);
  const updateRequestLinks = useStockRequestStore(s => s.updateRequestLinks);
  const allRequests = useStockRequestStore(s => s.requests);

  const [open, setOpen] = useState(true);
  const [linked, setLinked] = useState<StockRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkError, setLinkError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    const jobIds = relatedJobIds?.length ? relatedJobIds : [job.id];
    const results = await Promise.all(jobIds.map((id) => fetchRequestsForJob(id)));
    const merged = new Map<string, StockRequest>();
    results.flat().forEach((req) => merged.set(req.id, req));
    setLinked(Array.from(merged.values()).sort((a, b) => b.requestNumber - a.requestNumber));
    setLoading(false);
  }, [fetchRequestsForJob, job.id, relatedJobIds]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (linking && allRequests.length === 0) void fetchRequests();
  }, [linking, allRequests.length, fetchRequests]);

  const openRequest = (requestId: string) => {
    window.dispatchEvent(new CustomEvent('openStockRequest', { detail: { requestId } }));
  };

  const handleLink = async () => {
    if (!selectedRequestId) return;
    setSaving(true);
    setLinkError('');
    const ok = await updateRequestLinks(selectedRequestId, {
      siteId: site.id,
      jobId: job.id,
    });
    setSaving(false);
    if (!ok) {
      setLinkError(useStockRequestStore.getState().error ?? 'Could not link request.');
      return;
    }
    setSelectedRequestId('');
    setLinking(false);
    await reload();
  };

  const sortedPickerRequests = [...allRequests].sort((a, b) => b.requestNumber - a.requestNumber);
  const selectedRequest = allRequests.find(r => r.id === selectedRequestId);
  const selectedLinkedElsewhere = selectedRequest?.jobId && selectedRequest.jobId !== job.id;
  const otherJobTitle = selectedLinkedElsewhere
    ? resolveJobTitle(sites, selectedRequest?.siteId ?? null, selectedRequest?.jobId ?? null)
    : null;

  const selectCls = 'text-sm px-2.5 py-1.5 rounded border w-full focus:outline-none focus:ring-1 focus:ring-blue-500';
  const inputBg = isDark ? '#171a1f' : '#f9fafb';

  return (
    <>
      <div className="mx-5 my-2" style={{ height: 1, backgroundColor: border }} />

      <div className="px-5 pb-2">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 w-full text-left mb-1 transition-colors"
          style={{ color: muted }}
        >
          <ChevronRight
            size={14}
            style={{ transition: 'transform 180ms', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
          />
          <span className="text-[11px] uppercase tracking-wide font-medium">
            Stock Requests {!loading && `(${linked.length})`}
          </span>
        </button>

        {open && (
          <div className="pl-4 space-y-2">
            {loading ? (
              <p className="text-sm py-1" style={{ color: muted }}>Loading…</p>
            ) : linked.length === 0 ? (
              <p className="text-sm py-1" style={{ color: muted }}>
                No stock requests linked to this job yet. Link an existing request below, or create one in Stock Control.
              </p>
            ) : (
              <div className="space-y-1">
                {linked.map(req => {
                  const { received, total } = requestReceiveProgress(req);
                  const lineCount = (req.lines ?? []).filter(l => l.status !== 'cancelled').length;
                  return (
                    <button
                      key={req.id}
                      type="button"
                      onClick={() => openRequest(req.id)}
                      className="w-full text-left rounded px-2 py-2 transition-colors"
                      style={{ borderBottom: `1px solid ${divider}` }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? '#262b35' : '#f9fafb'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium" style={{ color: text }}>
                          {formatRequestRef(req.requestNumber)}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusBadge(req.status, isDark)}`}>
                          {fmtStatusLabel(req.status)}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5" style={{ color: muted }}>
                        {lineCount} line{lineCount === 1 ? '' : 's'}
                        {total > 0 && <> · Received {received}/{total}</>}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            {!linking ? (
              <button
                type="button"
                onClick={() => { setLinking(true); setLinkError(''); }}
                className="text-sm px-2.5 py-1.5 rounded border transition-colors mt-1"
                style={{ borderColor: border, color: text }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? '#262b35' : '#f9fafb'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                Link existing request
              </button>
            ) : (
              <div className="space-y-2 pt-1">
                <label className="text-sm block" style={{ color: muted }}>Choose a request</label>
                <select
                  value={selectedRequestId}
                  onChange={e => { setSelectedRequestId(e.target.value); setLinkError(''); }}
                  className={selectCls}
                  style={{ backgroundColor: inputBg, borderColor: border, color: selectedRequestId ? text : muted }}
                >
                  <option value="">Select request…</option>
                  {sortedPickerRequests.map(r => {
                    const linkedJob = r.jobId ? resolveJobTitle(sites, r.siteId, r.jobId) : null;
                    const suffix = linkedJob && r.jobId !== job.id
                      ? ` · linked to ${linkedJob}`
                      : r.jobId === job.id
                        ? ' · already linked here'
                        : '';
                    return (
                      <option key={r.id} value={r.id}>
                        {formatRequestRef(r.requestNumber)} · {fmtStatusLabel(r.status)}{suffix}
                      </option>
                    );
                  })}
                </select>
                {selectedLinkedElsewhere && otherJobTitle && (
                  <p className="text-sm" style={{ color: isDark ? '#fbbf24' : '#b45309' }}>
                    This request is linked to &ldquo;{otherJobTitle}&rdquo;. Linking will move it to this job.
                  </p>
                )}
                {linkError && <p className="text-sm text-red-400">{linkError}</p>}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleLink()}
                    disabled={saving || !selectedRequestId}
                    className="text-sm px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Linking…' : 'Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLinking(false); setSelectedRequestId(''); setLinkError(''); }}
                    disabled={saving}
                    className="text-sm px-2.5 py-1 rounded border transition-colors"
                    style={{ borderColor: border, color: text }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default JobStockRequestsSection;
